import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import useTranslations from '@/i18n/useTranslations';
import SubscriptionPreview from '@/pages/profile/channels/SubscriptionPreview';
import ChannelsService from '@/services/channels.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';

import {
  CHANNELS_DISCOVER_PATH,
  getChannelActivityTime,
  hydrateDiscoverChannel,
  normalizeChannelId,
} from '../utils/channelDiscovery';

import './styles/RecommendedChannelsSection.css';

const MAX_RECOMMENDED_CHANNELS = 4;
const ACTIVE_MONTHS_WINDOW = 6;
const RECOMMENDED_CHANNELS_CACHE_VERSION = 'v1';

function shuffleList(list) {
  const next = [...list];
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  return next;
}

function getTimeValue(value) {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function getDisplayTimestamp(value) {
  const timeValue = getTimeValue(value);
  if (!timeValue) return '';
  const date = new Date(timeValue);
  const isCurrentYear = date.getFullYear() === new Date().getFullYear();

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(isCurrentYear ? {} : { year: 'numeric' }),
  });
}

function getTodayCacheStamp() {
  return new Date().toISOString().slice(0, 10);
}

export default function RecommendedChannelsSection({
  hidden = false,
  excludedChannelIds = [],
  hasJoinedChannels = false,
  cacheScopeKey = 'anonymous',
}) {
  const navigate = useNavigate();
  const t = useTranslations('common.profile.channels');
  const [channels, setChannels] = useState([]);
  const [previewChannel, setPreviewChannel] = useState(null);
  const [loading, setLoading] = useState(false);

  const excludedIdsSet = useMemo(
    () => new Set((excludedChannelIds || []).map((value) => String(value))),
    [excludedChannelIds]
  );
  const cacheKey = useMemo(
    () => `recommended_channels_sidebar_${RECOMMENDED_CHANNELS_CACHE_VERSION}_${cacheScopeKey}`,
    [cacheScopeKey]
  );

  useEffect(() => {
    if (hidden) {
      setChannels([]);
      return undefined;
    }

    let cancelled = false;

    async function loadRecommendations() {
      const cachedValue = typeof window !== 'undefined' ? window.localStorage.getItem(cacheKey) : null;
      if (cachedValue) {
        try {
          const parsed = JSON.parse(cachedValue);
          if (parsed?.stamp === getTodayCacheStamp() && Array.isArray(parsed?.items)) {
            if (!cancelled) {
              setChannels(parsed.items.slice(0, MAX_RECOMMENDED_CHANNELS));
            }
            return;
          }
        } catch (_) {
          // Ignore invalid cache payloads and fetch fresh data below.
        }
      }

      setLoading(true);
      try {
        const { response, data } = await ChannelsService.getAll();
        if (!response?.ok) {
          if (!cancelled) setChannels([]);
          return;
        }

        const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        const cutoffDate = new Date();
        cutoffDate.setMonth(cutoffDate.getMonth() - ACTIVE_MONTHS_WINDOW);
        const cutoffTime = cutoffDate.getTime();

        const activeCandidates = list.filter((channel) => {
          const channelId = normalizeChannelId(channel?._id || channel?.id || channel?.channelId || channel?.channel_id);
          if (!channelId || excludedIdsSet.has(channelId)) return false;
          return getChannelActivityTime(channel) >= cutoffTime;
        });

        const randomizedCandidates = shuffleList(activeCandidates).slice(0, 12);
        const hydrated = [];

        for (const candidate of randomizedCandidates) {
          // Sequential hydration keeps the request burst controlled and lets us stop early.

          const resolved = await hydrateDiscoverChannel(candidate);
          if (!resolved) continue;
          hydrated.push(resolved);
          if (hydrated.length >= MAX_RECOMMENDED_CHANNELS) {
            break;
          }
        }

        if (!cancelled) {
          setChannels(hydrated);
          if (typeof window !== 'undefined') {
            window.localStorage.setItem(cacheKey, JSON.stringify({
              stamp: getTodayCacheStamp(),
              items: hydrated.slice(0, MAX_RECOMMENDED_CHANNELS),
            }));
          }
        }
      } catch (error) {
        console.error('Error loading recommended channels:', error);
        if (!cancelled) {
          setChannels([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadRecommendations();

    return () => {
      cancelled = true;
    };
  }, [cacheKey, excludedIdsSet, hidden]);

  const visibleChannels = useMemo(
    () => channels
      .filter((channel) => !excludedIdsSet.has(String(channel?._id)))
      .slice(0, MAX_RECOMMENDED_CHANNELS),
    [channels, excludedIdsSet]
  );

  if (hidden || (!loading && visibleChannels.length === 0)) {
    return null;
  }

  return (
    <section className="recommended-channels-section">
      <div className="recommended-channels-section__header">
        <div className="recommended-channels-section__title-row">
          <h2 className="recommended-channels-section__title">{t('recommended.title')}</h2>
          <ContinueButton
            className="recommended-channels-section__see-all-button"
            onClick={() => navigate(CHANNELS_DISCOVER_PATH)}
          >
            {t('recommended.see_all')}
          </ContinueButton>
        </div>
        <p className="recommended-channels-section__copy">
          {hasJoinedChannels
            ? t('recommended.copy_with_joined')
            : t('recommended.copy_without_joined')}
        </p>
      </div>

      <div className="recommended-channels-section__list">
        {visibleChannels.map((channel) => (
          <button
            type="button"
            key={channel._id}
            onClick={() => setPreviewChannel(channel)}
            className="recommended-channels-section__item"
          >
            <div className="recommended-channels-section__avatar">
              <UserProfileImage
                image={channel.ownerImage || channel.owner_image}
                typeOfProfile={channel.ownerType || channel.owner_user_type}
                className="recommended-channels-section__avatar-image"
              />
            </div>

            <div className="recommended-channels-section__body">
              <div className="recommended-channels-section__topline">
                <span className="recommended-channels-section__name">{channel.title}</span>
                <span className="recommended-channels-section__time">
                  {getDisplayTimestamp(channel.lastActivity)}
                </span>
              </div>

              <div className="recommended-channels-section__owner">
                @{channel.ownerUsername || channel.owner_username}
              </div>
            </div>
          </button>
        ))}
      </div>
      {previewChannel && (
        <SubscriptionPreview
          channelId={previewChannel._id}
          ownerUsername={previewChannel.ownerUsername || previewChannel.owner_username}
          onClose={() => setPreviewChannel(null)}
        />
      )}
    </section>
  );
}
